import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { useAuth, DEMO_ACCOUNTS } from "@/context/AuthContext";
import { Lu } from "@/components/shared";
import { LogoMark, ThemeToggle } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const HERO = "https://images.unsplash.com/photo-1605237165959-dcc784975d51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBob3RlbCUyMHJvb20lMjBzYXVkaSUyMGFyYWJpYSUyMHJpeWFkaCUyMGludGVyaW9yJTIwZHVza3xlbnwwfHx8fDE3ODg5MzkwNzF8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { t, lang, toggle } = useLang();
  const { login, loginAs } = useAuth();
  const [email, setEmail] = useState("hisham8@gmail.com");
  const [password, setPassword] = useState("Madarek@123");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); }
    catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  const quick = async (acc) => {
    setBusy(true);
    try { await loginAs(acc.email); }
    catch (err) { toast.error("Login failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row">
      {/* Visual side */}
      <div className="relative lg:w-1/2 min-h-[240px] lg:min-h-screen overflow-hidden">
        <img src={HERO} alt="luxury hotel" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(6,10,18,0.55) 0%, rgba(6,10,18,0.85) 100%)" }} />
        <div className="relative z-10 p-8 lg:p-14 h-full flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={44} className="md-gold-glow rounded-xl" />
            <div>
              <p className="text-2xl font-extrabold text-white tracking-[0.18em] leading-none">MADAREK</p>
              <p className="text-xs md-teal-text font-semibold tracking-[0.3em] mt-1">SMART</p>
            </div>
          </div>
          <div className="hidden lg:block max-w-md">
            <h2 className="text-4xl font-extrabold text-white leading-tight">
              {lang === "ar" ? "منصة الضيافة الذكية للسعودية" : "Smart hospitality platform for Saudi Arabia"}
            </h2>
            <p className="mt-4 text-white/85 leading-relaxed">
              {lang === "ar"
                ? "مبنية فوق Tuya IoT مع طبقة ذكاء مدارك — توفير طاقة، تجربة نزيل أفضل، وتشغيل مُدار بالبيانات."
                : "Built on Tuya IoT with the Madarek Intelligence Layer — energy savings, better guest experience, data-driven operations."}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {["30% ⚡", "99.9% SLA", "PDPL ✓"].map((b) => (
                <span key={b} className="px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-white/10 border border-white/25 backdrop-blur">{b}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="lg:w-1/2 flex items-center justify-center p-6 lg:p-14">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-md">
          <div className="flex justify-end gap-2 mb-4">
            <ThemeToggle />
            <button onClick={toggle} data-testid="login-lang-toggle"
              className="px-3 py-1.5 rounded-lg bg-foreground/5 border border-border text-sm font-semibold text-foreground/80">
              {lang === "ar" ? "English" : "العربية"}
            </button>
          </div>
          <h1 className="text-3xl font-extrabold text-foreground">{t("loginTitle")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t("loginSub")}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">{t("email")}</label>
              <Input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="bg-foreground/5 border-border text-foreground h-11" placeholder="you@hotel.sa" />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">{t("password")}</label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="bg-foreground/5 border-border text-foreground h-11" placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full h-11 md-gold-gradient text-primary-foreground font-bold hover:opacity-90 border-0">
              {busy ? t("signingIn") : t("login")}
            </Button>
          </form>

          <div className="mt-8">
            <p className="text-xs uppercase tracking-wider text-muted-foreground/70 mb-3">{t("demoAccounts")}</p>
            <div className="grid grid-cols-2 gap-2.5">
              {DEMO_ACCOUNTS.map((a) => (
                <button key={a.role} data-testid={`demo-${a.role}`} onClick={() => quick(a)} disabled={busy}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-foreground/5 hover:bg-foreground/10 border border-border hover:border-primary/30 text-sm text-foreground/80 transition-colors text-start">
                  <Lu name={a.icon} className="w-4 h-4 text-primary shrink-0" />
                  <span className="truncate">{a[lang === "ar" ? "label_ar" : "label_en"]}</span>
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted-foreground/70 text-center">{t("liveData")} · Tuya adapter ready</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
